import type { Shop_Artist } from '@app/data/shop-bff';
import Avatar from '@redbubble/design-system/src/Avatar';
import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import Stack from '@redbubble/design-system/src/Stack';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { LocalisableLink } from '../../../../components/LocalisableLink';
import { LoadingBoundary } from '../LoadingBoundary';
import styles from './ListingInfo.module.scss';

export interface ListingInfoProps
  extends Partial<Pick<Shop_Artist, 'name' | 'avatar' | 'profileUrl'>> {
  productTitle: string;
}

export const messages = defineMessages({
  designedAndSoldBy: {
    defaultMessage: 'Designed and sold by <link>{artistName}</link>',
  },
});

export const ListingInfo = ({
  avatar: artistAvatar,
  name: artistName,
  profileUrl: artistProfileUrl,
  productTitle,
}: ListingInfoProps) => {
  const intl = useIntl();
  const hasAllData =
    artistAvatar && artistName && artistProfileUrl && productTitle.trim();

  if (!hasAllData) return null;

  return (
    <Stack spacing="xxs">
      <Box className={styles.title}>
        <LoadingBoundary
          loadingComponent={<Skeleton height={31} animated roundedCorners />}
        >
          <Text element="h1" type="display3">
            {productTitle}
          </Text>
        </LoadingBoundary>
      </Box>
      <Stack
        direction={constants.HORIZONTAL}
        alignItems="center"
        spacing="xs"
        width="100%"
        className={styles.artistInfo}
      >
        <LoadingBoundary
          loadingComponent={
            <Skeleton height={32} width={32} animated circular />
          }
        >
          <Avatar src={artistAvatar} size={constants.TINY} />
        </LoadingBoundary>
        <Box className={styles.artistName}>
          <LoadingBoundary
            loadingComponent={
              <Skeleton height={22} width={200} animated roundedCorners />
            }
          >
            <Text type="body2">
              {intl.formatMessage(messages.designedAndSoldBy, {
                link: LocalisableLink(artistProfileUrl),
                artistName,
              })}
            </Text>
          </LoadingBoundary>
        </Box>
      </Stack>
    </Stack>
  );
};
